import { Button, LoadingSpinner } from "pepsico-ds";
import { useCallback, useEffect, useRef, useState } from "react";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import { useHasAccessPermission } from "../../hooks/useHasAccessPermission";
import { useTierRules } from "../../hooks/useTierRules";
import { useTiers } from "../../hooks/useTiers";
import { useToast } from "../../hooks/useToast";
import TierEvaluationModel from "./TierEvaluationModel";
import "./TierManagementPage.scss";
import TierQualificationLevels from "./TierQualificationLevels";
import { TIER_STATUS } from "./tierMockData";

// ----- Confirmation Modal (Activate / Inactivate) -----
function ConfirmationModal({
  title,
  description,
  warningItems,
  onConfirm,
  onCancel,
}) {
  const [understood, setUnderstood] = useState(false);

  return (
    <div className="confirm-modal-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-modal-header">
          <h3>{title}</h3>
          <button className="modal-close-btn" onClick={onCancel} aria-label="Close">
            &#10005;
          </button>
        </div>
        <div className="confirm-modal-body">
          {description && <p className="confirm-description">{description}</p>}
          <div className="warning-box">
            <div className="warning-box-header">
              <span className="warning-triangle">&#9888;&#65039;</span>
              <strong>Warning</strong>
            </div>
            <div className="warning-box-content">
              {warningItems.prefix && <p>{warningItems.prefix}</p>}
              <ul>
                {warningItems.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p>Do you want to continue?</p>
            </div>
          </div>
          <div className="confirm-checkbox">
            <input
              type="checkbox"
              checked={understood}
              onChange={() => setUnderstood((p) => !p)}
              id="understood-checkbox"
            />
            <label htmlFor="understood-checkbox">
              I understand and I want to continue
            </label>
          </div>
        </div>
        <div className="confirm-modal-footer">
          <Button size="medium" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={onConfirm}
            disabled={!understood}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TierManagementPage() {
  const { tiers, loading: tiersLoading, saveTiers } = useTiers();

  const {
    tierRules,
    loading: rulesLoading,
    handleUpdateTierRules,
    handleActivateTierRules,
    handleDeactivateTierRules,
    saving: rulesSaving,
    lifecycleOperation,
    lifecycleCompletion,
    lifecycleInProgress,
  } = useTierRules();

  const { toast, showToast, closeToast } = useToast();
  const hasAccess = useHasAccessPermission();
  const hasTierSettingsPermission = hasAccess(
    "permissions.configure_program_settings"
  );

  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showInactivateModal, setShowInactivateModal] = useState(false);
  const [showEditTiersModal, setShowEditTiersModal] = useState(false);
  const reportedLifecycleCompletion = useRef(null);

  const tierStatus = tierRules?.status || TIER_STATUS.INACTIVE;
  const isActive = tierStatus === TIER_STATUS.ACTIVE;
  const lifecycleAction =
    lifecycleOperation?.operationType === "INACTIVATE"
      ? "Inactivation"
      : lifecycleOperation?.operationType === "ACTIVATE"
        ? "Activation"
        : "Tier update";
  const hasTierCurrency =
    Boolean(tierRules?.currencyCode) &&
    Number.isInteger(Number(tierRules?.currencyExponent));

  useEffect(() => {
    if (
      !lifecycleCompletion ||
      reportedLifecycleCompletion.current === lifecycleCompletion.operationId
    ) {
      return;
    }
    reportedLifecycleCompletion.current = lifecycleCompletion.operationId;

    const activation = lifecycleCompletion.operationType === "ACTIVATE";
    if (lifecycleCompletion.succeeded) {
      showToast(
        "success",
        activation ? "Activated" : "Deactivated",
        activation
          ? "Tier structure activated successfully."
          : "Tier structure deactivated successfully."
      );
      return;
    }

    showToast(
      "error",
      "Tier operation did not complete",
      activation
        ? "Tier structure activation did not complete. Review the operation status for details."
        : "Tier structure deactivation did not complete. Review the operation status for details."
    );
  }, [lifecycleCompletion, showToast]);

  // Called when the Edit Tiers modal Apply button fires mutations
  const handleTiersApplyResult = useCallback(
    (error) => {
      setShowEditTiersModal(false);
      if (error) {
        showToast("error", "Error", error.message || "Failed to save tier changes");
      } else {
        showToast("success", "Saved", "Tiers updated successfully");
      }
    },
    [showToast]
  );

  // Evaluation model save — receives the full draft from TierEvaluationModel
  const handleSaveEvaluationModel = useCallback(
    async (draft) => {
      try {
        const input = {
          qualificationMetric: draft.qualificationMetric,
          evaluationWindowType: draft.evaluationWindowType,
          immediateDowngradeEnabled: draft.immediateDowngradeEnabled,
          currencyCode: draft.currencyCode,
          excludedSkus: draft.excludedSkus || [],
          excludedProductGroups: draft.excludedProductGroups || [],
        };

        // Only pass rollingWindowMonths for ROLLING_X_MONTH_WINDOW
        if (draft.evaluationWindowType === "ROLLING_X_MONTH_WINDOW") {
          input.rollingWindowMonths = draft.rollingWindowMonths;
        }

        // Review date schedule
        if (draft.reviewDateSchedule) {
          const schedule = { type: draft.reviewDateSchedule.type };
          if (draft.reviewDateSchedule.type === "FIXED_DATE") {
            schedule.date = draft.reviewDateSchedule.date;
          }
          input.reviewDateSchedule = schedule;
        }

        // Historical qualification mode
        if (draft.historicalQualificationMode) {
          input.historicalQualificationMode = draft.historicalQualificationMode;
          if (draft.historicalQualificationMode === "ENABLED") {
            input.historicalQualificationMonths =
              draft.historicalQualificationMonths ?? 12;
          }
        }

        // Product group exception SKUs (array, clean __typename)
        if (draft.productGroupExceptionSkus) {
          input.productGroupExceptionSkus = (
            Array.isArray(draft.productGroupExceptionSkus)
              ? draft.productGroupExceptionSkus
              : [draft.productGroupExceptionSkus]
          ).map((item) => ({
            productGroupId: item.productGroupId,
            skuIds: item.skuIds,
          }));
        }

        await handleUpdateTierRules(input);
        showToast("success", "Saved", "Evaluation model updated successfully");
      } catch (err) {
        showToast(
          "error",
          "Error",
          err.message || "Failed to update evaluation model"
        );
      }
    },
    [handleUpdateTierRules, showToast]
  );

  // Activation
  const handleActivate = useCallback(async () => {
    setShowActivateModal(false);
    try {
      const response = await handleActivateTierRules(tierRules?.id);

      if (response?.outcome == null && response?.operationId) {
        showToast(
          "success",
          "Processing",
          "Tier structure activation is in progress. Controls will unlock when it completes."
        );
      } else if (response?.outcome === "COMPLETED") {
        showToast("success", "Activated", "Tier structure activated successfully.");
      }
    } catch (err) {
      showToast(
        "error",
        "Error",
        err.message || "Failed to activate tier structure"
      );
    }
  }, [tierRules?.id, handleActivateTierRules, showToast]);

  // Inactivation
  const handleInactivate = useCallback(async () => {
    setShowInactivateModal(false);
    try {
      const response = await handleDeactivateTierRules(tierRules?.id);

      if (response?.outcome == null && response?.operationId) {
        showToast(
          "success",
          "Processing",
          "Tier structure inactivation is in progress. Controls will unlock when it completes."
        );
      } else if (response?.outcome === "COMPLETED") {
        showToast(
          "success",
          "Deactivated",
          "Tier structure deactivated successfully."
        );
      }
    } catch (err) {
      showToast(
        "error",
        "Error",
        err.message || "Failed to inactivate tier structure"
      );
    }
  }, [tierRules?.id, handleDeactivateTierRules, showToast]);

  if (tiersLoading && !tiers.length && rulesLoading && !tierRules) {
    return (
      <div className="tier-management-page">
        <div className="tier-loading">
          <LoadingSpinner size={{ height: "40px", width: "40px" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="tier-management-page">
      {/* Page Title */}
      <h2 className="tier-page-title">Tier management</h2>

      {/* Tier Qualification Levels */}
      <div className="tier-section">
        <div className="section-header">
          <h3>Tier Qualification Levels</h3>
          {hasTierSettingsPermission && (
            <div className="section-actions">
              <Button
                size="small"
                variant="tertiary"
                onClick={() => setShowEditTiersModal(true)}
                disabled={!hasTierCurrency || lifecycleInProgress}
              >
                Edit tiers
              </Button>
              {lifecycleInProgress ? (
                <Button size="small" variant="secondary" disabled>
                  {lifecycleAction} in progress
                </Button>
              ) : !isActive ? (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => setShowActivateModal(true)}
                  disabled={!tiers.length}
                >
                  Activate Structure
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="secondary"
                  onClick={() => setShowInactivateModal(true)}
                >
                  Inactivate Structure
                </Button>
              )}
            </div>
          )}
        </div>
        <TierQualificationLevels
          tiers={tiers}
          currencyCode={tierRules?.currencyCode}
          currencyExponent={tierRules?.currencyExponent}
        />
      </div>

      {/* Tier Evaluation Model */}
      <div className="tier-section">
        <div className="section-header">
          <h3>Tier Evaluation Model</h3>
        </div>
        <TierEvaluationModel
          tierRules={tierRules}
          onUpdate={handleSaveEvaluationModel}
          loading={rulesLoading}
          saving={rulesSaving || lifecycleInProgress}
          hasEditPermission={hasTierSettingsPermission}
        />
      </div>

      {/* Edit Tiers Modal — mutations fire immediately on Apply */}
      {showEditTiersModal && (
        <TierQualificationLevels.EditModal
          tiers={tiers}
          serverTiers={tiers}
          isActive={isActive}
          currencyCode={tierRules?.currencyCode}
          currencyExponent={tierRules?.currencyExponent}
          saveTiers={saveTiers}
          onClose={() => setShowEditTiersModal(false)}
          onApply={handleTiersApplyResult}
        />
      )}

      {/* Activate Confirmation */}
      {showActivateModal && (
        <ConfirmationModal
          title="Activate tier structure?"
          description="Activating this tier structure will automatically assign tiers to newly enrolled loyalty members according to the configured rules"
          warningItems={{
            prefix: "After activation:",
            items: [
              "Tier qualification levels become locked.",
              "Qualification metric, Evaluation Window Type and Review Date become locked.",
              "The structure can be inactivated and reset.",
            ],
          }}
          onConfirm={handleActivate}
          onCancel={() => setShowActivateModal(false)}
        />
      )}

      {/* Inactivate Confirmation */}
      {showInactivateModal && (
        <ConfirmationModal
          title="Inactivating this tier structure will immediately remove all tier assignments"
          warningItems={{
            items: [
              "All assigned tiers will be removed.",
              "Members will no longer participate in the tier program.",
              "This action cannot be undone.",
            ],
          }}
          onConfirm={handleInactivate}
          onCancel={() => setShowInactivateModal(false)}
        />
      )}

      {/* Toast */}
      <ToastNotification
        open={toast.open}
        handleClose={closeToast}
        type={toast.type}
        title={toast.title}
        description={toast.description}
      />
    </div>
  );
}
